/**
 * Step 1: Dataset Selection
 *
 * Three ways to load a dataset:
 * 1. Browse Catalogue (302 curated datasets)
 * 2. Upload File (drag-and-drop)
 * 3. Load from URL
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Upload, Loader2, Link as LinkIcon, Database } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { geojsonDatasets, type GeoJsonDataset } from "@/data/geojsonDatasets";
import { getCountryFlag } from "@/utils/countryFlags";
import { fetchGeoJSONFromUrl, readGeoJSONFile } from "@/utils/geojsonLoader";
import { setDataset, nextStep } from "@/stores/useStudioStore";
import { useToast } from "@/hooks/use-toast";

export function Step1DatasetSelection() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("catalogue");
  const [searchTerm, setSearchTerm] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Filter datasets by search term
  const filteredDatasets = geojsonDatasets.filter((dataset) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      dataset.title.toLowerCase().includes(term) ||
      dataset.geography.toLowerCase().includes(term) ||
      dataset.description.toLowerCase().includes(term) ||
      dataset.topics.some((t) => t.toLowerCase().includes(term))
    );
  });

  // Handle dataset selection from catalogue
  const handleCatalogueSelect = useCallback(async (dataset: GeoJsonDataset) => {
    try {
      setIsLoading(true);
      const collection = await fetchGeoJSONFromUrl(dataset.path);

      setDataset(
        { type: "catalogue", id: dataset.id, path: dataset.path, topics: dataset.topics },
        collection,
        dataset.title
      );

      toast({
        title: "Dataset loaded",
        description: `${dataset.title} is ready to configure.`,
      });

      nextStep(); // Auto-advance to next step
    } catch (error) {
      toast({
        title: "Failed to load dataset",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Listen for auto-load event (triggered by URL parameter)
  useEffect(() => {
    const handleAutoLoad = (event: Event) => {
      const customEvent = event as CustomEvent<{ datasetId: string }>;
      const { datasetId } = customEvent.detail;

      const dataset = geojsonDatasets.find((d) => d.id === datasetId);
      if (dataset) {
        // Switch to catalogue tab and auto-load
        setActiveTab("catalogue");
        handleCatalogueSelect(dataset);
      }
    };

    window.addEventListener("autoLoadDataset", handleAutoLoad);
    return () => window.removeEventListener("autoLoadDataset", handleAutoLoad);
  }, [handleCatalogueSelect]);

  // Handle file upload
  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;

    const file = files[0];
    if (!file) return;

    try {
      setIsLoading(true);
      const collection = await readGeoJSONFile(file);

      setDataset(
        { type: "file", filename: file.name },
        collection,
        file.name
      );

      toast({
        title: "File uploaded",
        description: `${file.name} is ready to configure.`,
      });

      nextStep(); // Auto-advance to next step
    } catch (error) {
      toast({
        title: "Failed to upload file",
        description: error instanceof Error ? error.message : "Invalid GeoJSON file",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle URL load
  const handleUrlLoad = async () => {
    if (!urlInput.trim()) {
      toast({
        title: "URL required",
        description: "Please enter a GeoJSON URL",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      const collection = await fetchGeoJSONFromUrl(urlInput);

      setDataset(
        { type: "url", url: urlInput },
        collection,
        urlInput.split("/").pop() || "Custom dataset"
      );

      toast({
        title: "Dataset loaded",
        description: "GeoJSON loaded from URL successfully.",
      });

      nextStep(); // Auto-advance to next step
    } catch (error) {
      toast({
        title: "Failed to load URL",
        description: error instanceof Error ? error.message : "Invalid URL or GeoJSON",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const handleFilePicker = () => {
    fileInputRef.current?.click();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Choose Your Dataset</CardTitle>
        <p className="text-sm text-muted-foreground">
          Select from our catalogue, upload your own GeoJSON, or load from a URL
        </p>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="catalogue">
              <Database className="w-4 h-4 mr-2" />
              Browse Catalogue
            </TabsTrigger>
            <TabsTrigger value="upload">
              <Upload className="w-4 h-4 mr-2" />
              Upload File
            </TabsTrigger>
            <TabsTrigger value="url">
              <LinkIcon className="w-4 h-4 mr-2" />
              Load URL
            </TabsTrigger>
          </TabsList>

          {/* Tab 1: Browse Catalogue */}
          <TabsContent value="catalogue" className="space-y-4">
            <div>
              <Input
                placeholder="Search datasets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="h-[400px] rounded-md border overflow-y-auto">
              <div className="p-4 space-y-2">
                {filteredDatasets.map((dataset) => (
                  <button
                    key={dataset.id}
                    onClick={() => handleCatalogueSelect(dataset)}
                    disabled={isLoading}
                    className="w-full text-left p-4 rounded-lg border hover:border-primary hover:bg-accent/50 transition-all disabled:opacity-50"
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm">
                        <span className="mr-2">{getCountryFlag(dataset.geography)}</span>
                        {dataset.title}
                      </h3>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                        {dataset.description}
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="secondary" className="text-xs">
                          {dataset.featureCount} features
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {dataset.geography}
                        </Badge>
                      </div>
                    </div>
                  </button>
                ))}
                {filteredDatasets.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    No datasets found. Try a different search term.
                  </p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Tab 2: Upload File */}
          <TabsContent value="upload">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={handleFilePicker}
              className={`
                flex flex-col items-center justify-center gap-4
                rounded-lg border-2 border-dashed p-12 cursor-pointer
                transition-all
                ${dragActive ? "border-primary bg-accent" : "border-border hover:border-primary/50"}
              `}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-12 h-12 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Processing file...</p>
                </>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-primary" />
                  <div className="text-center">
                    <p className="font-semibold">Drag & drop your GeoJSON file</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or click to browse (.geojson, .json)
                    </p>
                  </div>
                  <Button type="button" variant="outline" disabled={isLoading}>
                    Browse Files
                  </Button>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/geo+json,.geojson,application/json,.json"
                className="hidden"
                onChange={handleFileChange}
                disabled={isLoading}
              />
            </div>
          </TabsContent>

          {/* Tab 3: Load URL */}
          <TabsContent value="url" className="space-y-4">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">GeoJSON URL</label>
                <Input
                  placeholder="https://example.com/data.geojson"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  disabled={isLoading}
                  className="mt-2"
                />
              </div>
              <Button
                onClick={handleUrlLoad}
                disabled={isLoading || !urlInput.trim()}
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Load Dataset"
                )}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
